import { BusinessCategory } from '@prisma/client';

export type ParsedCategorySlug = { kind: 'all' } | { kind: 'id'; id: number } | { category: BusinessCategory; kind: 'legacy' };

const isBusinessCategoryValue = (value: string): value is BusinessCategory => value in BusinessCategory;

/**
 * Resolves the `/jobs/category/[key]` URL segment. New links carry the numeric category
 * id; saved links and localStorage may still hold the legacy lowercase enum slug, which
 * is matched through the rows' legacyCategory. A legacy slug with no matching row — a
 * business with no category rows at all — resolves to the enum itself so filtering can
 * degrade to the old column. Anything unresolvable degrades to "all" rather than
 * erroring: a stale link should never strand the jobs page.
 */
export function parseCategorySlug(slug: string | undefined, categories: { id: number; legacyCategory: string | null }[]): ParsedCategorySlug {
	const cleaned = slug?.split('?')[0];

	if (!cleaned || cleaned === 'all') {
		return { kind: 'all' };
	}

	if (/^\d+$/.test(cleaned)) {
		const id = Number(cleaned);

		return categories.some((category) => category.id === id) ? { id, kind: 'id' } : { kind: 'all' };
	}

	const legacy = cleaned.toUpperCase();
	const matched = categories.find((category) => category.legacyCategory === legacy);

	if (matched) {
		return { id: matched.id, kind: 'id' };
	}

	return isBusinessCategoryValue(legacy) ? { category: legacy, kind: 'legacy' } : { kind: 'all' };
}

export type BusinessCategoryTab = {
	isActive: boolean;
	label: string;
	value: string;
};

export function buildBusinessCategoryTabs({
	activeSlug,
	getLabel,
	keys,
}: {
	activeSlug: string | undefined;
	getLabel: (key: string) => string;
	keys: string[];
}): BusinessCategoryTab[] {
	return keys.map((key) => ({
		isActive: activeSlug === key.toLowerCase(),
		label: getLabel(key),
		value: key.toLowerCase(),
	}));
}

export function resolveBusinessCategorySlug({ label, tabs }: { label: string; tabs: BusinessCategoryTab[] }): string | undefined {
	return tabs.find((tab) => tab.label === label)?.value;
}

export function jobCategoryPath(
	category: number | string | null | undefined,
	categories: { id: number | null; legacyCategory: string | null }[]
): string {
	const key = category == null ? '' : String(category);

	if (/^\d+$/.test(key)) {
		return `/jobs/category/${key}`;
	}

	const matched = categories.find((row) => row.id !== null && row.legacyCategory === key)?.id;

	return `/jobs/category/${matched ?? (key ? key.toLowerCase() : 'all')}`;
}
